import { useSecuritiesContext } from '@talos/kyoko';
import { kebabCase } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { getVisibleConditionalDefaultLayoutSpecs } from '../components/AppLayout/Layouts/conditionalLayoutSpecs';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import type { NavigationItem } from '../tokens/navigationPaths';
import type { LayoutSection, LayoutSpec } from '../types/LayoutConfig';
import { useDefaultSpaces } from './useDefaultSpaces';
import { useFeatureFlag } from './useFeatureFlag';
import { useRoleAuth } from './useRoleAuth';

export function toNavGroupName(groupName: string): string {
  const trimmed = groupName.trim();
  return kebabCase(trimmed);
}

export function useSpacesNavItems(layoutSection: LayoutSection): NavigationItem[] | undefined {
  const { layoutConfigs, spacesConfigs, initializeSpaces, updateSpacesConfigs } = useAppLayoutConfig(layoutSection);
  const { initialized } = spacesConfigs;
  const defaultSpacesToAdd = useDefaultSpaces(layoutSection, 'all');
  const newUserSpacesToAdd = useDefaultSpaces(layoutSection, 'new-users');
  const flags = useFeatureFlag();
  const { isAuthorized } = useRoleAuth();
  const securities = useSecuritiesContext();

  // Add default spaces to the layout configs stored in app config
  useEffect(() => {
    if (layoutConfigs == null || defaultSpacesToAdd == null || newUserSpacesToAdd == null || !flags.isLoaded) {
      return;
    }

    const existingIds = new Set(layoutConfigs.map(l => l.id));
    const toAdd: typeof layoutConfigs = [];

    for (const spec of defaultSpacesToAdd) {
      if (!existingIds.has(spec.Config.layoutSpec.id)) {
        toAdd.push({ ...spec.Config.layoutSpec, deviceType: undefined, isRemovable: true });
        existingIds.add(spec.Config.layoutSpec.id);
      }
    }

    // If the spaces are not initialized, add the new user spaces to the layout configs
    if (!initialized) {
      for (const spec of newUserSpacesToAdd) {
        if (!existingIds.has(spec.Config.layoutSpec.id)) {
          toAdd.push({ ...spec.Config.layoutSpec, deviceType: undefined, isRemovable: true });
          existingIds.add(spec.Config.layoutSpec.id);
        }
      }

      for (const layoutSpec of getVisibleConditionalDefaultLayoutSpecs(layoutSection, {
        flags,
        isAuthorized,
        securities,
      })) {
        if (!existingIds.has(layoutSpec.id)) {
          toAdd.push({ ...layoutSpec, isRemovable: true });
          existingIds.add(layoutSpec.id);
        }
      }
    }

    // Only update the layout configs if there are new spaces
    if (toAdd.length > 0) {
      updateSpacesConfigs([...layoutConfigs, ...toAdd]);
    }

    // Mark spaces as initialized after the first run to prevent re-adding removed spaces on subsequent runs
    if (!initialized) {
      initializeSpaces();
    }
  }, [
    initialized,
    layoutConfigs,
    defaultSpacesToAdd,
    newUserSpacesToAdd,
    updateSpacesConfigs,
    initializeSpaces,
    layoutSection,
    flags,
    isAuthorized,
    securities,
  ]);

  const spacesNavItems = useMemo(() => {
    if (!layoutConfigs) {
      return undefined;
    }

    const orderedLayouts = layoutConfigs;

    // Separate into ungrouped and grouped, preserving `layouts` array order
    const ungrouped: LayoutSpec[] = [];
    const grouped = new Map<string, LayoutSpec[]>();

    for (const layout of orderedLayouts) {
      const groupName = layout.group;
      if (!groupName) {
        ungrouped.push(layout);
      } else {
        const existing = grouped.get(groupName) ?? [];
        existing.push(layout);
        grouped.set(groupName, existing);
      }
    }

    const result: NavigationItem[] = [];

    // Ungrouped spaces — use the same pattern as dealer's first unlabeled group
    if (ungrouped.length > 0) {
      const children = ungrouped.map(layout => toSpaceNavItem(layout, layoutSection));
      result.push({
        path: `/${layoutSection}`,
        label: undefined,
        allowSpaceDrops: true,
        children,
      });
    }

    // Named groups — each gets its own path segment
    for (const [groupName, groupLayouts] of grouped) {
      const navGroupName = toNavGroupName(groupName);
      const children = groupLayouts.map(layout => toSpaceNavItem(layout, layoutSection, navGroupName));
      result.push({
        path: `/${layoutSection}/${navGroupName}`,
        label: groupName,
        allowSpaceDrops: true,
        children,
      });
    }

    return result;
  }, [layoutConfigs, layoutSection]);

  return spacesNavItems;
}

function toSpaceNavItem(layout: LayoutSpec, layoutSection: LayoutSection, navGroupName?: string): NavigationItem {
  const basePath = navGroupName ? `/${layoutSection}/${navGroupName}/${layout.id}` : `/${layoutSection}/${layout.id}`;

  return {
    id: layout.id,
    path: basePath,
    label: layout.label,
    isEditable: true,
    isRemovable: layout.isRemovable ?? true,
    deviceType: layout.deviceType,
    children: [
      {
        path: `${basePath}/*`,
        label: undefined,
      },
    ],
  };
}
